import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import { toExclusionTooltips } from '../../utils/account/netWorth';

import type { HouseholdNetWorthQuery, HouseholdNetWorthQueryVariables } from './__generated__/useHouseholdNetWorth.gql';

const HOUSEHOLD_NET_WORTH = gql`
  query HouseholdNetWorth {
    householdNetWorth {
      total
      asOf
      excluded {
        accountId
        reason
      }
    }
  }
`;

/**
 * The household's net worth as the server rolled it up, and the accounts it left out.
 *
 * Cache-first like the account list it sits over, so the total and the rows beneath it refresh
 * on the same events; the field has no identity in the cache and is evicted through
 * `evictHouseholdNetWorth` when the list changes. `netWorth` is undefined while unread and on
 * error, and holds the previous figure while an eviction's refetch is in flight.
 *
 * @returns `excludedAccounts` maps an account id to why it is not in the total; empty until read.
 */
export const useHouseholdNetWorth = () => {
  const { data, previousData, loading, error } = useQuery<HouseholdNetWorthQuery, HouseholdNetWorthQueryVariables>(
    HOUSEHOLD_NET_WORTH
  );

  // A failed read must not leave a stale total standing over rows that have since changed.
  const netWorth = error ? undefined : (data ?? previousData)?.householdNetWorth;
  const excludedAccounts = React.useMemo(() => toExclusionTooltips(netWorth?.excluded), [netWorth]);

  return { netWorth, excludedAccounts, loading, error };
};
